// Full-screen overlay for the panel's cleaning cycle and nightly rest. Sits
// above the dashboard and the detail views but BELOW the alert toasts, so a
// new alert still shows (and chimes) while the screen is resting.
import { useEffect, useState } from 'react'
import { useKioskSummary } from '@/lib/kiosk'
import {
  CLEAN_MS, WAKE_MS, careAnchor, cleanField, restElapsedMs, scheduledCare,
} from '@/lib/kioskScreenCare'

// Fixed values, independent of the kiosk theme: the overlay is a panel
// exercise on pure fields, and the rest screen must stay black in light mode.
const REST_INK = '#6b6b6b'
const REST_ALERT = '#b3414d'

export function ScreenCare({ manualUntil, onStopManual }: {
  /** Epoch ms until which a manually started cleaning cycle runs (0 = none). */
  manualUntil: number
  onStopManual: () => void
}) {
  const [now, setNow] = useState(() => Date.now())
  const [wakeUntil, setWakeUntil] = useState(0)
  const { data: summary } = useKioskSummary()

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000)
    return () => clearInterval(id)
  }, [])

  const manual = now < manualUntil
  const date = new Date(now)
  const state = manual ? 'clean' : now < wakeUntil ? 'off' : scheduledCare(date)
  if (state === 'off') return null

  const anchor = careAnchor(now)
  const dismiss = () => (manual ? onStopManual() : setWakeUntil(Date.now() + WAKE_MS))

  if (state === 'clean') {
    const elapsed = manual ? now - (manualUntil - CLEAN_MS) : (restElapsedMs(date) ?? 0)
    return (
      <button
        onClick={dismiss}
        aria-label="Interrompre le soin de l'écran"
        className="absolute inset-0 z-[15] block h-full w-full"
        style={{ background: cleanField(elapsed), transition: 'background-color 2s linear' }}
      >
        {/* `difference` keeps the label readable on every field. */}
        <span
          className="absolute text-[11px] font-semibold uppercase tracking-[0.18em]"
          style={{ left: `${anchor.left}%`, top: `${anchor.top}%`, color: '#ffffff', mixBlendMode: 'difference', opacity: 0.55 }}
        >
          Soin de l'écran · toucher pour interrompre
        </span>
      </button>
    )
  }

  const open = summary?.alerts?.open ?? 0
  return (
    <button
      onClick={dismiss}
      aria-label="Réveiller l'écran"
      className="absolute inset-0 z-[15] block h-full w-full"
      style={{ background: '#000000' }}
    >
      <span
        className="absolute flex items-baseline gap-3"
        style={{ left: `${anchor.left}%`, top: `${anchor.top}%`, transition: 'left 4s ease-in-out, top 4s ease-in-out' }}
      >
        <span className="kmono text-4xl font-bold leading-none" style={{ color: REST_INK }}>
          {date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </span>
        {open > 0 && (
          <span className="kmono text-lg font-bold" style={{ color: REST_ALERT }}>
            {open} alerte{open > 1 ? 's' : ''}
          </span>
        )}
      </span>
    </button>
  )
}
